import { AddressFormState } from '@spa/components/organisms/profile/addressFormState'
import { NameFormState } from '@spa/components/organisms/profile/nameFormState'
import { ApiContact, ContactService } from '@spa/services/api/contact'
import { convertToRegionName } from '@spa/services/region'
import { Either, left, right } from 'fp-ts/lib/Either'

interface ContactFormState {
  nameForm: NameFormState
  addressForm: AddressFormState
  tel: string
}

export interface InitializeContactFormDeps {
  contactService: ContactService
}

export interface InitializeContactFormParam {
  contactId: string
}

export type InitializeContactFormErrorType = 'api' | 'fetch-null-data'

type InitializeContactForm = (deps: InitializeContactFormDeps) =>
  (param: InitializeContactFormParam) => Promise<Either<
    InitializeContactFormErrorType,
    ContactFormState
  >>
const initializeContactForm: InitializeContactForm = ({ contactService }) =>
  async ({ contactId }) => {

    const result = await contactService.get(contactId)
    if (result.isLeft()) {
      return left('api')
    }

    if (!result.value.body || !result.value.body.data) {
      return left('fetch-null-data')
    }

    const data = result.value.body.data

    return right({
      nameForm: {
        firstName: data.first_name,
        lastName: data.last_name,
      },
      addressForm: {
        zipcode: data.postal_code,
        prefectureCode: data.region_code,
        city: data.locality,
        town: data.street_address,
      },
      tel: data.tel,
    })
  }

export class AddressFieldsEnabledState {
  static init(addressFormValues: Partial<AddressFormState>): AddressFieldsEnabledState {
    return new AddressFieldsEnabledState(false, addressFormValues)
  }

  private constructor(
    private searchAddressDone: boolean,
    private addressFormValues: Partial<AddressFormState>
  ) {}
  searchedAddress(): AddressFieldsEnabledState {

    return new AddressFieldsEnabledState(true, this.addressFormValues)
  }

  /** フォームのenabled/disabled */
  get enabled(): boolean {

    return this.alreadyFilled || this.searchAddressDone
  }

  private get alreadyFilled(): boolean {

    return (
      this.addressFormValues.prefectureCode !== undefined &&
      this.addressFormValues.prefectureCode !== '' &&
      this.addressFormValues.city !== undefined &&
      this.addressFormValues.city !== '' &&
      this.addressFormValues.town !== undefined &&
      this.addressFormValues.town !== ''
    )
  }
}

export interface RegisterContactDeps {
  contactService: ContactService
  /**
   * invalidInputValidator に vee-validate のメソッドは直接指定しないこと。
   * 例えば、呼び出し側で { invalidInputValidator: this.$validator.validateAll, ... } の形で呼び出すと、
   * validateAllメソッド内のthisのコンテクストがvee-validateの想定外のものになり、エラーが起きる。
   *
   * また、invalidInputValidator の関数内に vee-validate のメソッドを書く場合、
   * registerContact 実行中はフォームをv-ifで消さないこと。
   * v-ifでフォームが消えていると validation する対象がないため、validation が常に通る可能性がある。
   */
  invalidInputValidator: () => Promise<boolean>
}

export interface RegisterContactParam {
  contactFormState: ContactFormState
}

export type RegisterContactErrorType = 'invalid-input' | 'api'

type RegisterContact = (deps: RegisterContactDeps) =>
  (param: RegisterContactParam) => Promise<Either<RegisterContactErrorType, ContactFormState>>
const registerContact: RegisterContact = ({ contactService, invalidInputValidator }) =>
  async ({ contactFormState }) => {

    if (!await invalidInputValidator()) {
      return left('invalid-input')
    }

    const result = await contactService.create(
      createPayloadFromFormState(contactFormState)
    )
    if (result.isLeft()) {
      return left('api')
    }

    return right(contactFormState)
  }

export interface UpdateContactDeps {
  contactService: ContactService
  /**
   * invalidInputValidator に vee-validate のメソッドは直接指定しないこと。
   * 例えば、呼び出し側で { invalidInputValidator: this.$validator.validateAll, ... } の形で呼び出すと、
   * validateAllメソッド内のthisのコンテクストがvee-validateの想定外のものになり、エラーが起きる。
   *
   * また、invalidInputValidator の関数内に vee-validate のメソッドを書く場合、
   * updateContact 実行中はフォームをv-ifで消さないこと。
   * v-ifでフォームが消えていると validation する対象がないため、validation が常に通る可能性がある。
   */
  invalidInputValidator: () => Promise<boolean>
}

export interface UpdateContactParam {
  contactId: string,
  contactFormState: ContactFormState
}

export type UpdateContactErrorType = 'invalid-input' | 'api'

type UpdateContact = (deps: UpdateContactDeps) =>
  (param: UpdateContactParam) => Promise<Either<UpdateContactErrorType, ContactFormState>>
const updateContact: UpdateContact = ({ contactService, invalidInputValidator }) =>
  async ({ contactId, contactFormState }) => {

    if (!await invalidInputValidator()) {
      return left('invalid-input')
    }

    const result = await contactService.update(
      contactId,
      createPayloadFromFormState(contactFormState)
    )
    if (result.isLeft()) {
      return left('api')
    }

    return right(contactFormState)
  }

function createPayloadFromFormState(formState: ContactFormState): ApiContact {
  return {
    is_delivery: true,
    display_level: 0,
    location_kind: 0,
    first_name: formState.nameForm.firstName,
    last_name: formState.nameForm.lastName,
    postal_code: sanitizeZipCode(formState.addressForm.zipcode),
    region: convertToRegionName(formState.addressForm.prefectureCode),
    region_code: formState.addressForm.prefectureCode,
    locality: formState.addressForm.city,
    street_address: formState.addressForm.town,
    tel: formState.tel,
  }
}

function sanitizeZipCode(zipCode: string): string {
  if (!zipCode) {
    return zipCode
  }

  return zipCode.replace('-', '')
}

export const interaction = {
  initializeContactForm,
  registerContact,
  updateContact,
}
